import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select, Tooltip } from "antd";
import type React from "react";
import styled from "styled-components";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  margin-top: 6px;

  .ant-select-selector {
    min-height: 42px !important;
    padding: 4px 11px !important;
    border-radius: 6px !important;
    border: 1px solid var(--color-primary-desat) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: var(--color-primary-dark) !important;
    box-shadow: none !important;
  }
`;

const OPTIONS = [
  { value: "__empty__", label: "—" },
  { value: "true", label: "True" },
  { value: "false", label: "False" },
] as const;

export interface BooleanFieldProps {
  label: string;
  name: string;
  value: boolean | undefined;
  onChange: (value: boolean | undefined) => void;
  required?: boolean;
  tooltip?: string;
  placeholder?: string;
}

const BooleanField: React.FC<BooleanFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  placeholder = "Select...",
}) => {
  const selectValue =
    value === undefined ? "__empty__" : value === true ? "true" : "false";

  const handleChange = (v: string | undefined) => {
    if (v === undefined || v === "__empty__") {
      onChange(undefined);
    } else if (v === "true") {
      onChange(true);
    } else if (v === "false") {
      onChange(false);
    } else {
      onChange(undefined);
    }
  };

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <StyledSelect
        id={name}
        placeholder={placeholder}
        value={selectValue}
        onChange={(v) => handleChange(typeof v === "string" ? v : undefined)}
        options={[...OPTIONS]}
        allowClear
        aria-required={required}
      />
    </FieldWrapper>
  );
};

export default BooleanField;
